// UI strings for our own voices (src/voices/, the shadowing loop in src/pronunciation/): the
// "meidän ääntämys" label, listening and dictation in the partner's voice, our recordings where
// Peterson's is missing, and the shadowing loop. Finnish (default) and English side by side.
// Style: docs/FINNISH-STYLE.md. Names (from /api/me) are never inflected: the sentences are built
// so a name stands in the nominative.
//
// Also "voices together" (2026-10-05): partner feedback per word, voice letters, show scenes as
// role play, progress over 15 months (one area: the two voice branches shared the file name).
import { defineArea } from '../area';

const MONTHS_FI = ['tammikuu', 'helmikuu', 'maaliskuu', 'huhtikuu', 'toukokuu', 'kesäkuu', 'heinäkuu', 'elokuu', 'syyskuu', 'lokakuu', 'marraskuu', 'joulukuu'];
const MONTHS_EN = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const parts = (ym: string): [number, number] => {
  const [y, m] = ym.split('-').map(Number);
  return [y ?? 0, (m ?? 1) - 1];
};

export const voicesFi = {
  /** The label on every recording of ours: never official. */
  ours: 'meidän ääntämys',
  oursHint: 'Teidän oma ääntämyksenne, ei virallinen äänite. Vertailukohta on aina Petersonin äänite.',
  you: 'sinä',
  peterson: 'Petersonin äänite',
  play: 'Kuuntele',
  pause: 'Tauko',
  failed: 'Äänitystä ei voitu toistaa. Jos olet ilman yhteyttä, lataa äänet laitteelle Kuuntele-sivulla.',
  first: {
    label: 'Kumman ääni ensin',
    short: 'Ensin:',
    mine: 'Oma',
  },
  partner: {
    h: 'Kumppanisi äänellä',
    intro: (name: string, n: number) =>
      n === 1
        ? `${name} on äänittänyt yhden sanan tai lauseen, jota voit harjoitella.`
        : `${name} on äänittänyt ${n} sanaa tai lausetta, joita voit harjoitella.`,
    mixed: (n: number) =>
      n === 1
        ? 'Harjoittele-kierroksilla on mukana 1 kumppanisi äänittämä repliikki.'
        : `Harjoittele-kierroksilla on mukana ${n} kumppanisi äänittämää repliikkiä.`,
    none: (name: string) =>
      `${name} ei ole vielä äänittänyt mitään, mitä täällä voisi kuunnella. Äänittää voi sanakirjassa, kohtausten repliikeissä ja Ääntäminen-sivulla.`,
    noPartner: 'Kumppanisi äänitykset tulevat tänne, kun olette molemmat kirjautuneet sovellukseen.',
    /** Nothing of the partner's to listen to yet: one line, no explanation (Axel's no-filler rule). */
    empty: 'Ei vielä äänityksiä.',
    loading: 'Haetaan äänityksiä…',
  },
  dictation: {
    h: 'Sanelu',
    intro: 'Kuuntele kumppanisi äänitys ja kirjoita se valyriaksi.',
    start: (n: number) => (n === 1 ? 'Aloita sanelu (1 tehtävä)' : `Aloita sanelu (${n} tehtävää)`),
    label: 'Kirjoita valyriaksi, mitä kuulit',
    kind: 'Sanelu',
  },
  missing: {
    h: 'Ilman Petersonin äänitettä',
    count: (n: number) => (n === 1 ? '1 sana tai lause' : `${n} sanaa tai lausetta`),
  },
  practise: {
    open: 'Äänitä itse',
    close: 'Sulje äänitys',
  },
  shadow: {
    h: 'Varjostus',
    hint: 'Petersonin äänite ja heti perään oma yrityksesi. Hidastus koskee vain Petersonin äänitettä.',
    once: 'Peterson, sitten sinä',
    loop: 'Toista vuorotellen',
    stop: 'Pysäytä',
    tempo: 'Petersonin tempo',
    rate: (r: number) => `${String(r).replace('.', ',')}×`,
    nowA: 'Nyt soi Peterson',
    nowB: 'Nyt soi sinun äänityksesi',
    round: (n: number) => `kierros ${n}`,
    again: 'Äänitä uudelleen',
    stopRec: 'Lopeta äänitys',
    failed: 'Toisto ei onnistunut. Napauta uudelleen.',
  },
  offline: {
    have: (n: number, of: number) =>
      n === of
        ? of === 1
          ? 'Ainoa oma äänityksenne on tällä laitteella.'
          : `Kaikki ${of} omaa äänitystänne ovat tällä laitteella.`
        : `Omia äänityksiänne tällä laitteella ${n}/${of}.`,
  },

  // ======== voices together ========

  // ---- partner feedback per word ----
  markLabel: 'Napauta sanaa, joka kuulosti oudolta',
  markHint: 'Merkitty sana tulee kumppanillesi harjoitukseksi. Toinen napautus poistaa merkinnän.',
  markNote: 'Huomio sanasta (valinnainen)',
  markNotePlaceholder: 'esim. ū jäi lyhyeksi, paino osui ensimmäiselle tavulle',
  markNoteSave: 'Tallenna huomio',
  markRemove: 'Poista merkintä',
  marked: 'merkitty',
  markedBy: (name: string) => `${name} merkitsi nämä sanat:`,
  markedByMe: 'Merkitsit nämä sanat:',
  marksTitle: 'Kumppanin huomiot',
  marksIntro: (name: string) =>
    `${name} merkitsi nämä sanat äänityksistäsi. Kuuntele edellinen äänityksesi, äänitä uudelleen ja vertaa. Uusi tallennettu äänitys kuittaa huomion.`,
  markItem: 'Kumppanin huomio:',
  previous: 'Edellinen äänityksesi',
  playPrevious: 'Kuuntele edellinen',
  textMissing: 'Tekstiä ei löydy tältä laitteelta.',
  inboxMarks: (n: number): string => (n === 1 ? 'kumppanin huomio ääntämisestäsi' : `${n} kumppanin huomiota ääntämisestäsi`),

  // ---- voice letters ----
  readAloud: 'Lue kirje ääneen',
  readAloudIntro: 'Äänitä kirje omalla äänelläsi. Kumppanisi kuuntelee sen ensin ja kirjoittaa, mitä kuuli, ennen kuin näkee tekstin.',
  voiceRow: 'Ääniviesti · kuuntele ensin',
  listenFirst: 'Kuuntele ensin',
  listenFirstIntro: (name: string) => `${name} luki tämän kirjeen ääneen. Kuuntele ja kirjoita, mitä kuulit. Teksti avautuu sen jälkeen.`,
  playLetter: 'Kuuntele kirje',
  typeLabel: 'Mitä kuulit',
  check: 'Tarkista',
  heardEmpty: 'Kirjoita ensin, mitä kuulit.',
  openText: 'Avaa teksti',
  skip: 'Avaa teksti kuuntelematta',
  result: (ok: number, total: number) => `Kuulit oikein ${ok}/${total} sanaa.`,
  resultNear: (n: number) =>
    n === 1 ? 'Yksi sana oli muuten oikein, mutta vokaalin pituus erosi.' : `${n} sanaa oli muuten oikein, mutta vokaalin pituus erosi.`,
  verdict: { ok: 'oikein', near: 'vokaalin pituus', wrong: 'eri sana', missed: 'puuttui' },
  typedAs: (typed: string) => `kirjoitit: ${typed}`,
  extra: 'Ylimääräiset sanat:',
  heardTitle: 'Mitä kuulit',

  // ---- show scenes as role play ----
  sceneTitle: 'Kohtaus roolipelinä',
  sceneIntro: 'Lukekaa viikon kohtaus ääneen: kumpikin äänittää omien hahmojensa repliikit.',
  sceneProposed: 'Viikon ehdotus',
  sceneChosenByMe: 'Valitsit tämän kohtauksen teille molemmille.',
  sceneChosenBy: (name: string) => `${name} valitsi tämän kohtauksen teille molemmille.`,
  scenePick: 'Kohtaus',
  sceneWithAudio: 'Petersonin äänitteet',
  sceneWithoutAudio: 'Ilman Petersonin äänitteitä',
  roles: 'Kuka lukee kenenkin repliikit',
  lineYours: 'sinun repliikkisi',
  linePartner: (name: string) => `${name} lukee`,
  recordLine: 'Äänitä repliikki',
  closeLine: 'Sulje',
  noTake: 'ei vielä äänitetty',
  takeBy: (name: string) => `äänittänyt ${name}`,
  playScene: 'Toista kohtaus meidän äänillämme',
  playPeterson: 'Toista Petersonin äänitteet',
  playActors: 'Toista näyttelijöiden äänin · vain kuunteluun',
  stop: 'Pysäytä',
  playing: (n: number, total: number) => `Repliikki ${n}/${total}`,
  missingTakes: (n: number) => (n === 1 ? 'Yksi repliikki on vielä äänittämättä, ja se ohitetaan.' : `${n} repliikkiä on vielä äänittämättä, ja ne ohitetaan.`),
  /** No takes yet (fix batch D2): why "Toista kohtaus meidän äänillämme" cannot play. */
  noTakesYet: (total: number) => `0/${total} repliikkiä äänitetty`,
  noPeterson: 'Tästä kohtauksesta ei ole Petersonin äänitteitä.',
  noScenes: 'Kohtauksia ei löydy tältä laitteelta.',
  saveFailed: 'Valintaa ei voitu tallentaa. Se vaatii yhteyden.',

  // ---- progress over 15 months ----
  progressTitle: 'Edistyminen ääninäytteinä',
  thisMonth: (month: string) => `Tämän kuun lauseet (${month})`,
  monthDone: 'Tämän kuun lauseet on äänitetty.',
  recorded: 'äänitetty',
  notRecorded: 'äänittämättä',
  record: 'Äänitä',
  close: 'Sulje',
  timeline: 'Aikajana',
  whose: 'Kenen äänitykset',
  compareTo: 'Vertaa kuukauteen',
  baseline: (month: string) => `Lähtökohta: ${month}`,
  noMonthTake: 'ei äänitystä',
  playBoth: 'Ensin lähtökohta, sitten valittu kuukausi',
  reminder: (done: number, total: number, monthIn: string) => `Kuukauden ääninäytteet: ${done}/${total} lausetta äänitetty ${monthIn}.`,
  reminderLink: 'Äänitä ne',
  month: (ym: string) => {
    const [y, m] = parts(ym);
    return `${MONTHS_FI[m]} ${y}`;
  },
  monthIn: (ym: string) => `${MONTHS_FI[parts(ym)[1]]}ssa`,
};

export type VoicesMessages = typeof voicesFi;

export const voicesEn: VoicesMessages = {
  ours: 'our pronunciation',
  oursHint: 'Your own pronunciation, not an official recording. Peterson’s recording is always the reference.',
  you: 'you',
  peterson: 'Peterson’s recording',
  play: 'Listen',
  pause: 'Pause',
  failed: 'The recording could not be played. If you are offline, download the audio on the Listen page first.',
  first: {
    label: 'Whose voice first',
    short: 'First:',
    mine: 'Mine',
  },
  partner: {
    h: 'In your partner’s voice',
    intro: (name, n) =>
      n === 1 ? `${name} has recorded one word or sentence for you to practise.` : `${name} has recorded ${n} words or sentences for you to practise.`,
    mixed: (n) => (n === 1 ? 'Practice rounds include 1 line your partner recorded.' : `Practice rounds include ${n} lines your partner recorded.`),
    none: (name) => `${name} has not recorded anything to listen to here yet. You can record in the dictionary, on scene lines and on the Pronunciation page.`,
    noPartner: 'Your partner’s recordings appear here once you have both signed in.',
    empty: 'No recordings yet.',
    loading: 'Fetching the recordings…',
  },
  dictation: {
    h: 'Dictation',
    intro: 'Listen to your partner’s recording and write it in High Valyrian.',
    start: (n) => (n === 1 ? 'Start dictation (1 item)' : `Start dictation (${n} items)`),
    label: 'Write in High Valyrian what you heard',
    kind: 'Dictation',
  },
  missing: {
    h: 'Without Peterson’s recording',
    count: (n) => (n === 1 ? '1 word or sentence' : `${n} words or sentences`),
  },
  practise: {
    open: 'Record it yourself',
    close: 'Close recording',
  },
  shadow: {
    h: 'Shadowing',
    hint: 'Peterson’s recording and your attempt right after it. Slowing down applies to Peterson’s recording only.',
    once: 'Peterson, then you',
    loop: 'Play in turns',
    stop: 'Stop',
    tempo: 'Peterson’s tempo',
    rate: (r) => `${r}×`,
    nowA: 'Now playing: Peterson',
    nowB: 'Now playing: your recording',
    round: (n) => `round ${n}`,
    again: 'Record again',
    stopRec: 'Stop recording',
    failed: 'Playback failed. Tap again.',
  },
  offline: {
    have: (n, of) =>
      n === of
        ? of === 1
          ? 'Your only recording is on this device.'
          : `All ${of} of your own recordings are on this device.`
        : `${n}/${of} of your own recordings on this device.`,
  },

  // ======== voices together ========

  markLabel: 'Tap the word that sounded off',
  markHint: 'A marked word becomes a practice item for your partner. Tap again to remove the mark.',
  markNote: 'Note on the word (optional)',
  markNotePlaceholder: 'e.g. the ū was short, the stress fell on the first syllable',
  markNoteSave: 'Save note',
  markRemove: 'Remove mark',
  marked: 'marked',
  markedBy: (name) => `${name} marked these words:`,
  markedByMe: 'You marked these words:',
  marksTitle: 'Your partner’s notes',
  marksIntro: (name) =>
    `${name} marked these words in your recordings. Listen to your previous take, record again and compare. A new saved recording clears the note.`,
  markItem: 'Partner’s note:',
  previous: 'Your previous take',
  playPrevious: 'Play previous',
  textMissing: 'The text is not on this device.',
  inboxMarks: (n) => (n === 1 ? 'a note from your partner on your pronunciation' : `${n} notes from your partner on your pronunciation`),

  readAloud: 'Read the letter aloud',
  readAloudIntro: 'Record the letter in your own voice. Your partner listens first and writes down what they heard before seeing the text.',
  voiceRow: 'Voice letter · listen first',
  listenFirst: 'Listen first',
  listenFirstIntro: (name) => `${name} read this letter aloud. Listen and write down what you heard. The text opens after that.`,
  playLetter: 'Play the letter',
  typeLabel: 'What you heard',
  check: 'Check',
  heardEmpty: 'Write down what you heard first.',
  openText: 'Open the text',
  skip: 'Open the text without listening',
  result: (ok, total) => `You heard ${ok} of ${total} words right.`,
  resultNear: (n) => (n === 1 ? 'One more word was right but for a vowel length.' : `${n} more words were right but for a vowel length.`),
  verdict: { ok: 'right', near: 'vowel length', wrong: 'another word', missed: 'missing' },
  typedAs: (typed) => `you wrote: ${typed}`,
  extra: 'Extra words:',
  heardTitle: 'What you heard',

  sceneTitle: 'Scene as role play',
  sceneIntro: 'Read the week’s scene aloud: each of you records your own characters’ lines.',
  sceneProposed: 'This week’s suggestion',
  sceneChosenByMe: 'You chose this scene for both of you.',
  sceneChosenBy: (name) => `${name} chose this scene for both of you.`,
  scenePick: 'Scene',
  sceneWithAudio: 'Peterson’s recordings',
  sceneWithoutAudio: 'Without Peterson’s recordings',
  roles: 'Who reads which lines',
  lineYours: 'your line',
  linePartner: (name) => `${name} reads`,
  recordLine: 'Record the line',
  closeLine: 'Close',
  noTake: 'not recorded yet',
  takeBy: (name) => `recorded by ${name}`,
  playScene: 'Play the scene in our voices',
  playPeterson: 'Play Peterson’s recordings',
  playActors: 'Play in the actors’ voices · listening only',
  stop: 'Stop',
  playing: (n, total) => `Line ${n} of ${total}`,
  missingTakes: (n) => (n === 1 ? 'One line is not recorded yet; it is skipped.' : `${n} lines are not recorded yet; they are skipped.`),
  noTakesYet: (total) => `0/${total} lines recorded`,
  noPeterson: 'Peterson has no recordings of this scene.',
  noScenes: 'No scenes on this device.',
  saveFailed: 'The choice could not be saved. It needs a connection.',

  progressTitle: 'Progress you can hear',
  thisMonth: (month) => `This month’s sentences (${month})`,
  monthDone: 'This month’s sentences are recorded.',
  recorded: 'recorded',
  notRecorded: 'not recorded',
  record: 'Record',
  close: 'Close',
  timeline: 'Timeline',
  whose: 'Whose recordings',
  compareTo: 'Compare with',
  baseline: (month) => `Starting point: ${month}`,
  noMonthTake: 'no recording',
  playBoth: 'Starting point, then the chosen month',
  reminder: (done, total, monthIn) => `This month’s voice samples: ${done} of ${total} sentences recorded ${monthIn}.`,
  reminderLink: 'Record them',
  month: (ym) => {
    const [y, m] = parts(ym);
    return `${MONTHS_EN[m]} ${y}`;
  },
  monthIn: (ym) => `in ${MONTHS_EN[parts(ym)[1]]}`,
};

// Messages.voices (i18n/area.ts: collected by area-registry.ts)
export const area = defineArea('voices', voicesFi, voicesEn);
declare module '../area' {
  interface AreaMessages extends Record<typeof area.key, typeof voicesFi> {}
}
